import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Loader2, OctagonAlert, X } from "lucide-react";
import api from "@/lib/api";

const STATUS_NEXT = { pending: "in_progress", in_progress: "done", done: "pending", blocked: "in_progress" };
const STATUS_LABEL = { pending: "Pendiente", in_progress: "En curso", done: "Completada", blocked: "Bloqueada" };

export default function PreopeningModal({ branch, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/branches/${branch.branch_id}/preopening`); setData(data); }
    catch (_e) { toast.error("No se pudieron cargar las etapas"); }
  };
  useEffect(() => { load(); }, [branch.branch_id]); // eslint-disable-line

  const cycle = async (s) => {
    setBusy(s.key);
    try {
      await api.patch(`/branches/${branch.branch_id}/preopening/${s.key}`, { status: STATUS_NEXT[s.status] });
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
    finally { setBusy(null); }
  };

  const block = async (s) => {
    setBusy(s.key);
    try { await api.patch(`/branches/${branch.branch_id}/preopening/${s.key}`, { status: s.status === "blocked" ? "pending" : "blocked" }); await load(); onChanged?.(); }
    catch (_e) { toast.error("No se pudo actualizar"); }
    finally { setBusy(null); }
  };

  const saveNotes = async (s, notes) => {
    if (notes === (s.notes || "")) return;
    try { await api.patch(`/branches/${branch.branch_id}/preopening/${s.key}`, { notes }); } catch (_e) { toast.error("No se guardó la nota"); }
  };

  const saveTarget = async (v) => {
    try { await api.patch(`/branches/${branch.branch_id}/preopening`, { target_open_date: v }); await load(); onChanged?.(); toast.success("Fecha objetivo guardada"); }
    catch (_e) { toast.error("No se guardó la fecha"); }
  };

  const icon = (st) => st === "done" ? <CheckCircle2 size={16} className="text-emerald-400" /> : st === "in_progress" ? <Loader2 size={16} className="text-sky-400 animate-spin" /> : st === "blocked" ? <OctagonAlert size={16} className="text-rose-400" /> : <Circle size={16} className="text-neutral-600" />;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="preopening-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F13] border border-white/10 rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-300 font-mono mb-2">Etapas de pre-apertura</div>
            <h3 className="font-display text-2xl text-white">{branch.name}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="preopening-close"><X size={18} /></button>
        </div>
        {!data ? <div className="text-xs text-neutral-500 font-mono mt-6">Cargando…</div> : (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="h-2 rounded-full bg-white/6 overflow-hidden"><div className="h-full bg-sky-400 rounded-full transition-all" style={{ width: `${data.progress_pct}%` }} /></div>
                <div className="text-[11px] font-mono text-neutral-400 mt-1.5">{data.progress_pct}% completado</div>
              </div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono flex items-center gap-2">Apertura objetivo
                <input type="date" defaultValue={data.target_open_date || ""} onBlur={(e) => e.target.value !== (data.target_open_date || "") && saveTarget(e.target.value)} className="bg-transparent border border-white/15 rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#FFBF57] outline-none" data-testid="preopening-target-date" />
              </label>
            </div>
            <ol className="mt-6 space-y-2">
              {data.stages.map((s, i) => (
                <li key={s.key} className={`rounded-xl border p-3 ${s.status === "in_progress" ? "border-sky-400/30" : s.status === "done" ? "border-emerald-400/20" : s.status === "blocked" ? "border-rose-400/30" : "border-white/8"}`} data-testid={`stage-${s.key}`}>
                  <div className="flex items-center gap-3">
                    <button onClick={() => cycle(s)} disabled={busy === s.key} className="shrink-0" title="Cambiar estado" data-testid={`stage-toggle-${s.key}`}>{icon(s.status)}</button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${s.status === "done" ? "text-neutral-400 line-through" : "text-white"}`}><span className="font-mono text-neutral-600 mr-2">{String(i + 1).padStart(2, "0")}</span>{s.label}</div>
                      <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{STATUS_LABEL[s.status]}{s.completed_at ? ` · ${new Date(s.completed_at).toLocaleDateString("es-MX")}` : ""}</div>
                    </div>
                    <button onClick={() => block(s)} className={`text-[10px] uppercase tracking-widest font-mono px-2 py-1 border rounded ${s.status === "blocked" ? "border-rose-400/40 text-rose-300" : "border-white/10 text-neutral-500 hover:text-white"}`} data-testid={`stage-block-${s.key}`}>{s.status === "blocked" ? "Desbloquear" : "Bloquear"}</button>
                  </div>
                  <input defaultValue={s.notes || ""} onBlur={(e) => saveNotes(s, e.target.value)} placeholder="Nota interna (proveedor, fecha, pendiente…)" className="mt-2 w-full bg-transparent border border-white/8 rounded-lg px-3 py-1.5 text-xs text-neutral-300 focus:border-[#FFBF57] outline-none" data-testid={`stage-notes-${s.key}`} />
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-neutral-500 mt-4">Al marcar "Inauguración" como completada, la sucursal pasa automáticamente a estado <span className="text-emerald-300">Operando</span>.</p>
          </>
        )}
      </div>
    </div>
  );
}
